import ResponsiveImage from "../../responsive-image/ResponsiveImage";
import React, { useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import "./ContactCard.css";

// Import local icons
import GoogleMapsIcon from "../../../icons/GoogleMaps.webp";
import PhoneIcon from "../../../icons/Phone.png";
import MailIcon from "../../../icons/Mail.png";
import lineQr from "./Line.JPG";
import weChatQr from "./WeChat.JPG";

// Import translations
import enTranslations from "../en.json";
import deTranslations from "../de.json";

const ContactCard = () => {
  const { t, i18n } = useTranslation();
  const [qrService, setQrService] = useState(null);
  const qrDialogRef = useRef(null);
  const qrTitleId = useId();
  const messengerOptions = [
    { name: "LINE", image: lineQr, width: 900, height: 900 },
    { name: "WeChat", image: weChatQr, width: 888, height: 1191 },
  ];

  const openQrCode = (service) => {
    setQrService(service);
    qrDialogRef.current.showModal();
  };

  // Add translations for this component
  i18n.addResourceBundle("en", "contact", enTranslations, true, true);
  i18n.addResourceBundle("de", "contact", deTranslations, true, true);
  // vCard data as a string
  const vCardData = `BEGIN:VCARD
VERSION:3.0
N;CHARSET=UTF-8:Jacobi;Andreas;;Violinmaker;
FN;CHARSET=UTF-8:Violinmaker Andreas Jacobi
TEL;TYPE=CELL:+491796808605
TEL;TYPE=HOME,VOICE:+496722971982
ORG;CHARSET=UTF-8:Restorer & Dealer of stringed instruments
TITLE;CHARSET=UTF-8:Geigenbaumeister
TEL;TYPE=WORK,VOICE:+4917637100950
ADR;CHARSET=UTF-8;TYPE=WORK,PREF:;;Bischof Blum Platz 9;Geisenheim;Hessen;65366;Germany
EMAIL:kontakt@geigenbau-jacobi.de
URL:https://www.geigenbau-jacobi.de
END:VCARD`;

  // Function to download the vCard
  const downloadVCard = () => {
    const blob = new Blob([vCardData], { type: "text/vcard" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "Geigenbau_Andreas_Jacobi.vcf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="contact-info">
      <div className="contact-item">
        <div className="contact-icon-container">
          <a
            href="https://maps.app.goo.gl/rZwQCWCDcqv6Px9L6"
            target="_blank"
            rel="noopener noreferrer"
          >
            <ResponsiveImage
              sizes="30px"
              loading="eager"
              src={GoogleMapsIcon}
              alt=""
              className="contact-item-icon contact-item-icon-img"
            />
          </a>
        </div>
        <div
          className="contact-text focus-highlight"
          tabIndex={0}
          role="text"
          aria-label={t("contact:address_aria", "Adresse: {{address}}", {
            address: "Bischof-Blum-Platz 9, 65366 Geisenheim",
          })}
          style={{ pointerEvents: "none" }}
          onFocus={(e) => {
            e.target.style.pointerEvents = "auto";
          }}
          onBlur={(e) => {
            e.target.style.pointerEvents = "none";
          }}
        >
          Bischof-Blum-Platz 9, 65366 Geisenheim
        </div>
      </div>

      <div className="contact-item">
        <div className="contact-icon-container">
          <a
            href="tel:+491796808605"
            aria-label={t("contact:phone_aria", "Telefonnummer: {{number}}", {
              number: "+49 179 6808605",
            })}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
              focusable="false"
              className="contact-item-icon contact-item-icon-img"
            >
              <rect x="6" y="2" width="12" height="20" rx="2" />
              <path d="M10 5h4M11 19h2" />
            </svg>
          </a>
        </div>
        <div
          className="contact-text focus-highlight"
          tabIndex={0}
          role="text"
          aria-label={t("contact:phone_aria", "Telefonnummer: {{number}}", {
            number: "+49 179 6808605",
          })}
          style={{ pointerEvents: "none" }}
          onFocus={(e) => {
            e.target.style.pointerEvents = "auto";
          }}
          onBlur={(e) => {
            e.target.style.pointerEvents = "none";
          }}
        >
          +49 179 6808605
        </div>
      </div>

      <div className="contact-item">
        <div className="contact-icon-container">
          <a href="tel:+496722971982">
            <ResponsiveImage
              sizes="30px"
              loading="eager"
              src={PhoneIcon}
              alt=""
              className="contact-item-icon contact-item-icon-img"
            />
          </a>
        </div>
        <div
          className="contact-text focus-highlight"
          tabIndex={0}
          role="text"
          aria-label={t("contact:phone_aria", "Telefonnummer: {{number}}", {
            number: "+49 (0) 6722971982",
          })}
          style={{ pointerEvents: "none" }}
          onFocus={(e) => {
            e.target.style.pointerEvents = "auto";
          }}
          onBlur={(e) => {
            e.target.style.pointerEvents = "none";
          }}
        >
          +49 (0) 6722971982
        </div>
      </div>

      <div className="contact-item">
        <div className="contact-icon-container">
          <a href="mailto:kontakt@geigenbau-jacobi.de">
            <ResponsiveImage
              sizes="30px"
              loading="eager"
              src={MailIcon}
              alt=""
              className="contact-item-icon contact-item-icon-img"
            />
          </a>
        </div>
        <div
          className="contact-text focus-highlight"
          tabIndex={0}
          role="text"
          aria-label={t("contact:email_aria", "E-Mail-Adresse: {{email}}", {
            email: "kontakt@geigenbau-jacobi.de",
          })}
          style={{ pointerEvents: "none" }}
          onFocus={(e) => {
            e.target.style.pointerEvents = "auto";
          }}
          onBlur={(e) => {
            e.target.style.pointerEvents = "none";
          }}
        >
          kontakt@geigenbau-jacobi.de
        </div>
      </div>

      <div
        className="contact-messengers"
        aria-label={t("contact:messenger_options")}
        role="group"
      >
        {messengerOptions.map((service) => (
          <button
            key={service.name}
            type="button"
            className="messenger-button focus-highlight"
            aria-haspopup="dialog"
            aria-label={t("contact:show_qr", { service: service.name })}
            onClick={() => openQrCode(service)}
          >
            {service.name}
            <span className="messenger-qr-label">QR</span>
          </button>
        ))}
      </div>
      {createPortal(
        <dialog
          ref={qrDialogRef}
          className="messenger-dialog"
          aria-labelledby={qrTitleId}
          onClick={(event) => {
            if (event.target === event.currentTarget)
              qrDialogRef.current.close();
          }}
        >
          <div className="messenger-dialog-content">
            <button
              type="button"
              className="messenger-dialog-close focus-highlight"
              onClick={() => qrDialogRef.current.close()}
              aria-label={t("contact:close_button")}
              autoFocus
            >
              ×
            </button>
            <h2 id={qrTitleId}>{qrService?.name}</h2>
            <p>{t("contact:qr_instructions", { service: qrService?.name })}</p>
            {qrService && (
              <>
                <img
                  src={qrService.image}
                  alt={t("contact:qr_image_alt", { service: qrService.name })}
                  width={qrService.width}
                  height={qrService.height}
                />
                <a href={qrService.image} download={`${qrService.name}-QR.jpg`}>
                  {t("contact:save_qr")}
                </a>
              </>
            )}
          </div>
        </dialog>,
        document.body,
      )}

      <div className="vcard-download">
        <button
          className="vcard-button focus-highlight"
          onClick={downloadVCard}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              downloadVCard();
            }
          }}
          tabIndex={0}
          aria-label={t(
            "contact:vcard_button_aria",
            "vCard herunterladen: {{title}}",
            { title: t("contact:vcard_download") },
          )}
          title={t("contact:vcard_title")}
        >
          {t("contact:vcard_download")}
        </button>
      </div>
    </div>
  );
};

export default ContactCard;
